import React from 'react';
import { ChangeItem } from '../../helpers/utils/changeDetection';

interface ConfirmationDialogProps {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Callback when dialog is closed */
  onClose: () => void;
  /** Callback when changes are confirmed */
  onConfirm: () => void;
  /** Title of the dialog */
  title: string;
  /** Array of changes to display */
  changes: ChangeItem[];
  /** Whether the confirmation is in progress */
  loading?: boolean;
}

/**
 * A reusable confirmation dialog component that displays side-by-side differences
 * for changes made to features and parameters.
 * 
 * @example
 * ```tsx
 * const [showDialog, setShowDialog] = useState(false);
 * const changes = detectChanges(originalData, currentData, deletedItems, appConfig);
 * 
 * <ConfirmationDialog
 *   isOpen={showDialog}
 *   onClose={() => setShowDialog(false)}
 *   onConfirm={handleSave}
 *   title="Confirm Changes"
 *   changes={changes}
 *   loading={isSaving}
 * />
 * ```
 */
const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  changes,
  loading = false
}) => {
  if (!isOpen) return null;

  const prettifyKey = (key: string): string => {
    return key
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const getChangeIcon = (type: string) => {
    switch (type) {
      case 'added':
        return <i className="bi bi-plus-circle-fill text-success me-2"></i>;
      case 'removed':
        return <i className="bi bi-dash-circle-fill text-danger me-2"></i>;
      case 'modified':
        return <i className="bi bi-arrow-clockwise text-warning me-2"></i>;
      default:
        return <i className="bi bi-circle me-2"></i>;
    }
  };

  const getChangeBadge = (type: string) => {
    switch (type) {
      case 'added':
        return <span className="badge bg-success">Added</span>;
      case 'removed':
        return <span className="badge bg-danger">Removed</span>;
      case 'modified':
        return <span className="badge bg-warning text-dark">Modified</span>;
      default:
        return null;
    }
  };

  const renderChangeValue = (value: any) => {
    if (value === null || value === undefined) {
      return <span className="text-muted fst-italic">Not set</span>;
    }
    if (typeof value === 'boolean') {
      return <span className={`badge ${value ? 'bg-success' : 'bg-secondary'}`}>{value ? 'Enabled' : 'Disabled'}</span>;
    }
    if (typeof value === 'object') {
      return <code className="text-primary">{JSON.stringify(value)}</code>;
    }
    return <span className="text-primary">{String(value)}</span>;
  };

  const groupedChanges = changes.reduce((acc, change) => {
    if (!acc[change.featureKey]) {
      acc[change.featureKey] = [];
    }
    acc[change.featureKey].push(change);
    return acc;
  }, {} as Record<string, ChangeItem[]>);

  return (
    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
      <div className="modal-dialog modal-lg modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="bi bi-exclamation-triangle-fill text-warning me-2"></i>
              {title}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              disabled={loading}
            ></button>
          </div>
          
          <div className="modal-body">
            {changes.length === 0 ? (
              <div className="text-center py-4">
                <i className="bi bi-check-circle-fill text-success fs-1 mb-3"></i>
                <h6>No changes detected</h6>
                <p className="text-muted">The configuration appears to be unchanged.</p>
              </div>
            ) : (
              <div>
                <div className="alert alert-info">
                  <i className="bi bi-info-circle-fill me-2"></i>
                  <strong>Review Changes:</strong> Please review the following changes before confirming:
                </div>
                
                <div className="changes-container">
                  {Object.entries(groupedChanges).map(([featureKey, featureChanges]) => (
                    <div key={featureKey} className="change-group mb-4">
                      <div className="change-group-header d-flex align-items-center justify-content-between p-3 bg-light rounded">
                        <h6 className="mb-0 fw-bold">
                          <i className="bi bi-gear-fill text-primary me-2"></i>
                          {prettifyKey(featureKey)}
                        </h6>
                        <div className="d-flex gap-2">
                          {featureChanges.map((change, index) => (
                            <div key={index}>
                              {getChangeBadge(change.type)}
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      <div className="change-group-body">
                        {featureChanges.map((change, index) => (
                          <div key={index} className="change-item p-3 border-bottom">
                            <div className="d-flex align-items-center mb-2">
                              {getChangeIcon(change.type)}
                              <span className="fw-semibold">
                                {change.isFeature ? 'Feature' : `Parameter: ${change.paramKey}`}
                              </span>
                            </div>
                            
                            {change.type === 'modified' && (
                              <div className="row">
                                <div className="col-md-6">
                                  <small className="text-muted d-block mb-1">Previous Value:</small>
                                  <div className="p-2 bg-light rounded">
                                    {renderChangeValue(change.oldValue)}
                                  </div>
                                </div>
                                <div className="col-md-6">
                                  <small className="text-muted d-block mb-1">New Value:</small>
                                  <div className="p-2 bg-light rounded">
                                    {renderChangeValue(change.newValue)}
                                  </div>
                                </div>
                              </div>
                            )}
                            
                            {change.type === 'added' && (
                              <div>
                                <small className="text-muted d-block mb-1">New Value:</small>
                                <div className="p-2 bg-light rounded">
                                  {renderChangeValue(change.newValue)}
                                </div>
                              </div>
                            )}
                            
                            {change.type === 'removed' && (
                              <div>
                                <small className="text-muted d-block mb-1">Removed Value:</small>
                                <div className="p-2 bg-light rounded">
                                  {renderChangeValue(change.oldValue)}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={onConfirm}
              disabled={loading || changes.length === 0}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Confirming...
                </>
              ) : (
                <>
                  <i className="bi bi-check-circle me-2"></i>
                  Confirm Changes
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationDialog; 